/**
 * The wire shapes of `Rws.AiInsights.Host.Administration.ProviderAccessEndpoints` (AIA-109 phases 4 and 5): the access-group
 * catalogue, an engineer's own key commands and status, and the administrator's view, settings, pause, resume, keep, confirm
 * delete and flagged list.
 *
 * It does not use `adminRequest`, because these endpoints answer problems that must be told apart (409 with a `reason`, 503 with
 * a `reason` that separates the kill switch from the change bound, 502) and a successful confirm-delete is either 204 or 200 with
 * `dryRun`. A key's secret appears only in the body of the create, renew and rotate answers; this module never logs, caches or
 * puts a secret in a URL, and every key command is a `POST` with an empty body.
 */
import { apiFetch } from "./apiFetch";

/** One access group a provider offers. */
export interface AccessGroup {
  id: string;
  displayName: string;
  description: string;
  /** The ids a person calls each model in the group by; absent or empty when the provider does not list them. */
  models?: string[];
}

/** The access groups one provider offers. */
export interface ProviderAccessGroups {
  vendorId: string;
  groups: AccessGroup[];
  /** The base address a person calls the provider's models at; absent or null when there is none to show. */
  inferenceEndpoint?: string | null;
}

/** One key's metadata, never its secret. */
export interface CredentialView {
  credentialId: string;
  createdAt: string;
  expiresAt: string;
  active: boolean;
  acknowledged: boolean;
}

/** An engineer's own access at a provider that issues keys (`ProviderAccessView`). */
export interface ProviderAccessView {
  vendorId: string;
  /** `NotRequested`, `BeingSetUp`, `Active`, `Restricted`, `Paused` or `SwitchedOff`; unknown values are shown generically. */
  status: string;
  /** `AllowanceUsedUp` or `WaitingForAllowance` for `Restricted`; `SwitchedOff` or `KeyExpired` for `SwitchedOff`. */
  reason: string | null;
  groups: string[];
  renewEnabled: boolean;
  rotateEnabled: boolean;
  credentials: CredentialView[];
}

/** A key as issued: the only response that carries a secret. */
export interface IssuedCredential {
  credentialId: string;
  secret: string;
  secretVariableName: string;
  expiresAt: string;
  setup: Record<string, string>;
}

/** An open manual task for a person and provider (`ManualTaskSummary`). */
export interface ManualTaskView {
  id: string;
  personId: string;
  personName: string;
  personEmail: string;
  vendorId: string;
  action: string;
  instructions: string;
  reason: string;
  createdAt: string;
  waitingRequestIds: string[];
}

/** An administrator's view of a person's access at a provider (`ProviderAccessAdminView`). */
export interface ProviderAccessAdminView extends ProviderAccessView {
  vendorIdentity: string | null;
  flaggedForDeletionAt: string | null;
  flagReason: string | null;
  flagOpen: boolean;
  pausedAt: string | null;
  openManualTasks: ManualTaskView[];
}

/** One account flagged for deletion (`FlaggedProviderAccount`). */
export interface FlaggedAccount {
  personId: string;
  personName: string;
  vendorId: string;
  flaggedAt: string;
  reason: string | null;
  providerEnabled: boolean;
  credentialIds: string[];
}

/** The outcome of one provider-access call. */
export type AccessResult<TResponse> =
  | { kind: "ok"; response: TResponse }
  | { kind: "notFound" }
  | { kind: "denied"; reason: string | null }
  | { kind: "conflict"; reason: string | null; message: string }
  | { kind: "paused"; reason: string | null; message: string }
  | { kind: "failed"; message: string }
  | { kind: "error"; message: string };

interface ProblemBody {
  detail?: string;
  title?: string;
  reason?: string | null;
}

async function readProblem(response: Response): Promise<{ message: string; reason: string | null }> {
  try {
    const body = (await response.json()) as ProblemBody;
    return { message: body.detail ?? body.title ?? "", reason: body.reason ?? null };
  } catch {
    return { message: "", reason: null };
  }
}

async function accessRequest<TResponse>(path: string, init: RequestInit): Promise<AccessResult<TResponse>> {
  try {
    const headers: HeadersInit = { Accept: "application/json", ...(init.body ? { "Content-Type": "application/json" } : {}) };
    const response = await apiFetch(path, { credentials: "same-origin", cache: "no-store", ...init, headers });

    if (response.status === 404) {
      return { kind: "notFound" };
    }

    if (response.ok) {
      if (response.status === 204) {
        return { kind: "ok", response: undefined as TResponse };
      }

      return { kind: "ok", response: (await response.json()) as TResponse };
    }

    const problem = await readProblem(response);
    switch (response.status) {
      case 403:
        return { kind: "denied", reason: problem.reason };
      case 409:
        return { kind: "conflict", reason: problem.reason, message: problem.message };
      case 503:
        return { kind: "paused", reason: problem.reason, message: problem.message };
      case 502:
      case 500:
        return { kind: "failed", message: problem.message };
      default:
        return { kind: "error", message: problem.message };
    }
  } catch {
    return { kind: "error", message: "" };
  }
}

const vendorPath = (vendorId: string): string => encodeURIComponent(vendorId);
const userAccessPath = (personId: string, vendorId: string): string =>
  `/api/users/${encodeURIComponent(personId)}/providers/${vendorPath(vendorId)}/access`;

/** Calls `GET /api/providers/access-groups`. */
export async function fetchAccessGroupCatalogs(): Promise<AccessResult<ProviderAccessGroups[]>> {
  const result = await accessRequest<ProviderAccessGroups[]>("/api/providers/access-groups", { method: "GET" });
  return result.kind === "ok" && !Array.isArray(result.response) ? { kind: "error", message: "" } : result;
}

/** Calls `GET /api/providers/{vendorId}/access/mine`. */
export function fetchMyAccess(vendorId: string): Promise<AccessResult<ProviderAccessView>> {
  return accessRequest<ProviderAccessView>(`/api/providers/${vendorPath(vendorId)}/access/mine`, { method: "GET" });
}

/** Calls `POST /api/providers/{vendorId}/access/mine/credential/revoke`: every key of the person is deactivated and deleted. */
export function revokeMyCredentials(vendorId: string): Promise<AccessResult<undefined>> {
  return accessRequest<undefined>(`/api/providers/${vendorPath(vendorId)}/access/mine/credential/revoke`, { method: "POST" });
}

/** The three commands that issue a key. */
export type IssueCommand = "credential" | "credential/renew" | "credential/rotate";

/** Calls `POST /api/providers/{vendorId}/access/mine/credential` (create or regenerate), `…/renew` or `…/rotate`. */
export function issueMyCredential(vendorId: string, command: IssueCommand): Promise<AccessResult<IssuedCredential>> {
  return accessRequest<IssuedCredential>(`/api/providers/${vendorPath(vendorId)}/access/mine/${command}`, { method: "POST" });
}

/** Calls `POST /api/providers/{vendorId}/access/mine/credential/acknowledge`. */
export function acknowledgeMyCredential(vendorId: string): Promise<AccessResult<undefined>> {
  return accessRequest<undefined>(`/api/providers/${vendorPath(vendorId)}/access/mine/credential/acknowledge`, { method: "POST" });
}

/** Calls `GET /api/users/{personId}/providers/{vendorId}/access` (administrator). */
export function fetchPersonAccess(personId: string, vendorId: string): Promise<AccessResult<ProviderAccessAdminView>> {
  return accessRequest<ProviderAccessAdminView>(userAccessPath(personId, vendorId), { method: "GET" });
}

/** Calls `PUT /api/users/{personId}/providers/{vendorId}/access`. */
export function setPersonAccessToggles(personId: string, vendorId: string, renewEnabled: boolean, rotateEnabled: boolean): Promise<AccessResult<undefined>> {
  return accessRequest<undefined>(userAccessPath(personId, vendorId), { method: "PUT", body: JSON.stringify({ renewEnabled, rotateEnabled }) });
}

/** The administrator's commands that take no body. */
export type AdministerCommand = "pause" | "resume" | "keep" | "revoke";

/** Calls `POST …/access/pause`, `…/resume`, `…/keep` or `…/revoke` (deactivates and deletes every key the person holds; the account is kept). */
export function administerPersonAccess(personId: string, vendorId: string, command: AdministerCommand): Promise<AccessResult<undefined>> {
  return accessRequest<undefined>(`${userAccessPath(personId, vendorId)}/${command}`, { method: "POST" });
}

/** A confirmed delete that was simulated (dry run) rather than done. */
export interface DryRunOutcome {
  dryRun: true;
  reason: string | null;
  detail: string | null;
}

/** Calls `POST …/access/delete`: `ok` with `undefined` when deleted (204), or with a {@link DryRunOutcome} when simulated (200). */
export function confirmPersonAccessDelete(personId: string, vendorId: string): Promise<AccessResult<DryRunOutcome | undefined>> {
  return accessRequest<DryRunOutcome | undefined>(`${userAccessPath(personId, vendorId)}/delete`, { method: "POST" });
}

/** Calls `GET /api/providers/access/flagged`. */
export function fetchFlaggedAccounts(): Promise<AccessResult<FlaggedAccount[]>> {
  return accessRequest<FlaggedAccount[]>("/api/providers/access/flagged", { method: "GET" });
}

/** What switching a provider off did (`ProviderSwitchOffResponse`). */
export interface ProviderSwitchOffResponse {
  /** Whether the account is now flagged for deletion. False when the provider's writes are off, or a manual task stands in for the vendor change. */
  flagged: boolean;
  /** Whether the vendor change could not be made and a manual task was raised in its place. */
  manualTaskRaised: boolean;
  /** The allocation change request raised for the person and approved by the administrator. */
  requestId: string | null;
}

/**
 * Calls `POST …/access/switch-off` (`providers.administer`): switches only this provider off in the person's allocation and flags the account
 * for deletion. Nothing is deleted; the deletion is confirmed on the Provider access tab.
 */
export function switchOffPersonProvider(personId: string, vendorId: string): Promise<AccessResult<ProviderSwitchOffResponse>> {
  return accessRequest<ProviderSwitchOffResponse>(`${userAccessPath(personId, vendorId)}/switch-off`, { method: "POST" });
}
